/**
 * Helpers for reading and writing form values at a nested path.
 *
 * Form values are usually flat: { fieldId: value }. But when a field lives
 * inside a repeatable section, its value is stored under the section's array
 * slot: { sectionId: [{ fieldId: value }, ...] }.
 *
 * A "path" is the sequence of (sectionId, instanceIndex) pairs leading to a
 * scope, optionally terminated by a leaf field id. Examples:
 *   - []                                       — root values
 *   - ['emails', 0]                            — first instance of `emails` section
 *   - ['emails', 0, 'address']                 — `address` field inside that instance
 *   - ['contacts', 1, 'phones', 2, 'number']   — nested case
 *
 * The path is encoded as a flat array of strings/numbers; even indices are
 * sectionIds (or the final field id), odd indices are instance indices.
 *
 * Ported verbatim from
 * apps/web/src/components/views/DocumentView/valuePath.ts.
 */

export type PathSegment = string | number;
export type ValuePath = PathSegment[];

type Container = Record<string, unknown> | unknown[];

function isContainer(v: unknown): v is Container {
  return v !== null && typeof v === "object";
}

/** Read the value at the given path. Returns undefined if any segment is missing. */
export function valueAtPath(values: unknown, path: ValuePath): unknown {
  let cur: unknown = values;
  for (const seg of path) {
    if (!isContainer(cur)) return undefined;
    if (Array.isArray(cur)) {
      if (typeof seg !== "number") return undefined;
      cur = cur[seg];
    } else {
      cur = (cur as Record<string, unknown>)[String(seg)];
    }
  }
  return cur;
}

/**
 * Immutably set the value at `path` and return a new root values object.
 * Creates intermediate objects/arrays as needed (object for string segments,
 * array for number segments).
 */
export function setValueAtPath<T extends Record<string, unknown>>(
  values: T,
  path: ValuePath,
  value: unknown,
): T {
  if (path.length === 0) return values;
  const root: Record<string, unknown> = { ...values };

  function go(parent: Container, idx: number): unknown {
    const seg = path[idx];
    const isLast = idx === path.length - 1;
    const nextSeg = isLast ? undefined : path[idx + 1];

    if (Array.isArray(parent)) {
      const i = seg as number;
      const next = parent.slice();
      if (isLast) {
        next[i] = value;
      } else {
        const existing = next[i];
        const child: Container = isContainer(existing)
          ? Array.isArray(existing)
            ? existing.slice()
            : { ...existing }
          : typeof nextSeg === "number"
            ? []
            : {};
        next[i] = go(child, idx + 1);
      }
      return next;
    } else {
      const k = String(seg);
      const next: Record<string, unknown> = { ...parent };
      if (isLast) {
        next[k] = value;
      } else {
        const existing = next[k];
        const child: Container = isContainer(existing)
          ? Array.isArray(existing)
            ? existing.slice()
            : { ...existing }
          : typeof nextSeg === "number"
            ? []
            : {};
        next[k] = go(child, idx + 1);
      }
      return next;
    }
  }

  // Start with root being treated as an object container
  return go(root, 0) as T;
}

/**
 * Remove the value (or array element) at `path`. For object paths, deletes
 * the key; for array paths (last segment is a number), splices the element
 * out so subsequent indices shift down.
 */
export function removeAtPath<T extends Record<string, unknown>>(
  values: T,
  path: ValuePath,
): T {
  if (path.length === 0) return values;

  function go(parent: Container, idx: number): unknown {
    const seg = path[idx];
    const isLast = idx === path.length - 1;

    if (Array.isArray(parent)) {
      const i = seg as number;
      const next = parent.slice();
      if (isLast) {
        next.splice(i, 1);
      } else {
        const existing = next[i];
        if (!isContainer(existing)) return parent;
        const child: Container = Array.isArray(existing)
          ? existing.slice()
          : { ...existing };
        next[i] = go(child, idx + 1);
      }
      return next;
    } else {
      const k = String(seg);
      const next: Record<string, unknown> = { ...parent };
      if (isLast) {
        delete next[k];
      } else {
        const existing = next[k];
        if (!isContainer(existing)) return parent;
        const child: Container = Array.isArray(existing)
          ? existing.slice()
          : { ...existing };
        next[k] = go(child, idx + 1);
      }
      return next;
    }
  }

  return go({ ...values }, 0) as T;
}
